/* eslint-disable react/no-unstable-nested-components */
import {
    Button,
    Checkbox,
    Dropdown,
    DropdownContent,
    DropdownItem,
    DropdownTrigger,
    FilterButton,
    SearchInput,
    Table,
} from "@jhool-io/fe-components";
import * as React from "react";
import { ColumnDef } from "@tanstack/react-table";
import { useParams, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
    ClaimStatus,
    IClaimForRemit,
} from "../../../claims/types/claims.types";
import {
    BillStatus,
    BillStatusLabels,
} from "../../../billing/types/billing.types";
import {
    formatDate,
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../../utils/helpers";
import { useDebounce } from "../../../../hooks/helpers";
import { useFetchClaimsForRemit } from "../../../claims/hooks/claims.queries";
import ListState from "../../../../components/ListState/ListState";
import { useAddRemitClaims } from "../../hooks/remits.mutations";
import useToast from "../../../../hooks/useToast";
import { IClaimsReport } from "../../types/remits.types";
import Loader from "../../../../shared-ui/Loader/Loader";
import TableFiltersDisplay from "../../../../shared-ui/TableFiltersDisplay/TableFiltersDisplay";
import { getBillStatusFilterLabel } from "../../../billing/helpers/billing.helpers";
import { getClaimStatusFilterLabel } from "../../../claims/helpers/claims.helper";

interface Props {
    setShowAddRemitModal: (value: boolean) => void;
}

export default function SearchRemitClaims({ setShowAddRemitModal }: Props) {
    const params = useParams();
    const remitId = params.remitId || "";
    const [rowSelection, setRowSelection] = React.useState({});

    const [selectedItems, setSelectedItems] = React.useState<IClaimForRemit[]>(
        []
    );
    const [selectedClaimIds, setSelectedClaimIds] = React.useState<string[]>(
        []
    );
    const [searchString, setSearchString] = React.useState("");

    const [searchParams, setSearchParams] = useSearchParams();

    const billStatusFilter = searchParams.getAll("invoice_statuses[]") || [];
    const claimStatusFilter = searchParams.getAll("claim_statuses[]") || [];

    const queryClient = useQueryClient();

    // Toast for success and error states
    const { toast } = useToast();

    const addRemitClaims = useAddRemitClaims(remitId);

    const searchStringDebouncedValue = useDebounce(searchString, 500);

    const { data, isFetching, isSuccess, error } = useFetchClaimsForRemit(
        {
            search_string: searchStringDebouncedValue,
            include_invoice_status: true,
            invoice_statuses:
                billStatusFilter.length > 0 ? billStatusFilter : undefined,
            claim_statuses:
                claimStatusFilter.length > 0 ? claimStatusFilter : undefined,
        },
        Boolean(searchStringDebouncedValue)
    );

    const memoizedTableData = React.useMemo(() => {
        return [
            ...selectedItems,
            ...(data?.data || []).filter(
                (item) => !selectedClaimIds.includes(item.claim_id)
            ),
        ];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.data]);

    const buildSuccessMessage = (
        addedClaims: string[],
        unAddedClaims: IClaimsReport[]
    ) => {
        const addedClaimsMessage =
            addedClaims.length > 0
                ? `${addedClaims.length} Claim(s) added`
                : "";
        const unAddedClaimsMessage =
            unAddedClaims.length > 0
                ? `${unAddedClaims.length} Claims(s) were not added because they have not been submitted`
                : "";
        const message = `Update complete: ${[
            addedClaimsMessage,
            unAddedClaimsMessage,
        ]
            .filter(Boolean)
            .join(", ")}`;
        return message;
    };

    const handleAddRemitClaims = () => {
        const payload = {
            claim_ids: selectedClaimIds || [],
        };
        addRemitClaims.mutate(payload, {
            onSuccess: (res) => {
                queryClient.removeQueries({ queryKey: ["remit-claims"] });
                queryClient.removeQueries({ queryKey: ["batch-remits"] });

                toast({
                    mode: "success",
                    message: `${buildSuccessMessage(
                        res.data.added_claims,
                        res.data.unadded_claims
                    )}`,
                });
                setShowAddRemitModal(false);
            },
            onError: (err) => {
                toast({
                    mode: "error",
                    message:
                        err.response?.data.message || "Could not add remit.",
                });
            },
        });
    };

    const handleBillStatusChange = (status: string) => {
        if (billStatusFilter.includes(status)) {
            searchParams.delete("invoice_statuses[]", status);
        } else {
            searchParams.append("invoice_statuses[]", status);
        }
        setSearchParams(searchParams);
    };

    const handleClaimStatusChange = (status: string) => {
        if (claimStatusFilter.includes(status)) {
            searchParams.delete("claim_statuses[]", status);
        } else {
            searchParams.append("claim_statuses[]", status);
        }
        setSearchParams(searchParams);
    };

    const columns: ColumnDef<IClaimForRemit>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    label=""
                    name="row"
                    value="row"
                    isChecked={table.getIsAllRowsSelected()}
                    indeterminate={table.getIsSomeRowsSelected()}
                    handleChange={table.getToggleAllPageRowsSelectedHandler()}
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    handleChange={() => {
                        row.getToggleSelectedHandler();
                        setSelectedClaimIds((prev) => {
                            return !prev.includes(row.original.claim_id)
                                ? [...prev, row.original.claim_id]
                                : prev.filter(
                                      (val) => val !== row.original.claim_id
                                  );
                        });
                        setSelectedItems((prev) => {
                            return !prev.includes(row.original)
                                ? [...prev, row.original]
                                : prev.filter((val) => val !== row.original);
                        });
                    }}
                    indeterminate={row.getIsSomeSelected()}
                    label=""
                    name="row"
                    value="row"
                    isChecked={selectedClaimIds.includes(row.original.claim_id)}
                />
            ),
        },
        {
            accessorKey: "client_name",
            header: "Client Name",
        },
        {
            accessorKey: "date_of_service",
            header: "Date Of Service",
            cell: ({ row }) => formatDate(row.original.date_of_service),
        },
        {
            accessorKey: "cpt_code",
            header: "CPT code",
        },
        {
            accessorKey: "external_reference_id",
            header: "Claim ID",
            cell: ({ row }) => row.original.external_reference_id ?? "--",
        },
        {
            accessorKey: "charge_amount",
            header: "Amount",
            cell: ({ row }) => showMoneyInAppFormat(row.original.charge_amount),
        },
        {
            accessorKey: "insurance_name",
            header: "Insurance",
            cell: ({ row }) => (
                <span className="uppercase">
                    {makeStringFirstLetterCapital(row.original.insurance_name)}
                </span>
            ),
        },
        {
            accessorKey: "claim_status",
            header: "Claim Status",
            cell: ({ row }) =>
                makeStringFirstLetterCapital(
                    removeEnumUnderscore(row.original.claim_status)
                ),
        },
        {
            accessorKey: "invoice_status",
            header: "Bill Status",
            cell: ({ row }) => {
                const invoiceStatus = row.original.invoice_status;

                return invoiceStatus
                    ? BillStatusLabels[invoiceStatus as BillStatus] ||
                          makeStringFirstLetterCapital(
                              removeEnumUnderscore(invoiceStatus)
                          )
                    : "--";
            },
        },
        {
            accessorKey: "requested_service",
            header: "Service",
            cell: ({ row }) =>
                makeStringFirstLetterCapital(
                    removeEnumUnderscore(row.original.appointment_type)
                ) || "--",
        },
    ];

    return (
        <div className="px-16">
            <SearchInput
                placeholder="Search by client name or claim ID"
                containerClass="mb-[--spacing-items]"
                onChange={(e) => setSearchString(e.target.value)}
            />

            <div className="flex items-center gap-[--spacing-items] mb-[--spacing-items]">
                <Dropdown>
                    <DropdownTrigger asChild>
                        <FilterButton>Bill Status</FilterButton>
                    </DropdownTrigger>
                    <DropdownContent align="start" width="auto">
                        {Object.values(BillStatus).map((status) => (
                            <DropdownItem
                                key={status}
                                onClick={() => handleBillStatusChange(status)}
                                isMulti
                                itemValue={status}
                                values={billStatusFilter}
                            >
                                {getBillStatusFilterLabel(status)}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>

                <Dropdown>
                    <DropdownTrigger asChild>
                        <FilterButton>Claim Status</FilterButton>
                    </DropdownTrigger>
                    <DropdownContent align="start" width="auto">
                        {Object.values(ClaimStatus).map((status) => (
                            <DropdownItem
                                key={status}
                                onClick={() => handleClaimStatusChange(status)}
                                isMulti
                                itemValue={status}
                                values={claimStatusFilter}
                            >
                                {getClaimStatusFilterLabel(status)}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                </Dropdown>
            </div>

            <TableFiltersDisplay
                appliedFilters={[
                    {
                        key: "invoice_statuses[]",
                        label: "Bill Status",
                        values: billStatusFilter.map(
                            (s) => BillStatusLabels[s] || s
                        ),
                        preserveValues: true,
                    },
                    {
                        key: "claim_statuses[]",
                        label: "Claim Status",
                        values: claimStatusFilter,
                    },
                ]}
                wrapperClass="mb-[--spacing-header]"
            />

            {error && error?.response?.status !== 404 && (
                <ListState
                    isError
                    stateHelperText="Try reloading this page to solve this issue"
                    errorMsg="Cannot display claims at this time please try again later"
                    context="general"
                />
            )}

            {selectedItems.length === 0 && isFetching && <Loader />}
            {!isFetching && data && isSuccess && data.data.length === 0 && (
                <ListState
                    stateHelperText="Search results will appear here"
                    isError={false}
                    emptyMessage="There are no claims for the current client you are searching for"
                    context="general"
                />
            )}
            {selectedItems?.length > 0 ||
            (data && !isFetching && isSuccess && data.data.length > 0) ? (
                <>
                    <Table
                        columns={columns}
                        data={memoizedTableData}
                        setRowSelection={setRowSelection}
                        rowSelection={rowSelection}
                    />

                    <div className="flex items-center justify-end gap-[--spacing-items] mt-[--spacing-header]">
                        <Button
                            variant="secondary"
                            onClick={() => setShowAddRemitModal(false)}
                        >
                            Cancel
                        </Button>
                        <Button onClick={handleAddRemitClaims}>
                            Add selected claims
                        </Button>
                    </div>
                </>
            ) : (
                <div>
                    {!searchString && !isFetching && (
                        <div>
                            <div className="flex items-center justify-center flex-col my-[90px] gap-2 max-w-[200px] mx-auto">
                                <svg
                                    viewBox="0 0 24 24"
                                    width="24"
                                    height="24"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    className="size-5 stroke-textSec"
                                >
                                    <circle cx="11" cy="11" r="8" />
                                    <line
                                        x1="21"
                                        y1="21"
                                        x2="16.65"
                                        y2="16.65"
                                    />
                                </svg>
                                <p className="text-base font-medium text-center text-textSec">
                                    Search for a client to add a claim manually
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
